import { DataProvider, GetListResponse, GetOneResponse } from '@refinedev/core';
import { axiosInstance } from '@/services/api';

// Resource endpoint mappings
const resourceMap: Record<string, string> = {
  players: 'admin/players',
  tournaments: 'admin/tournaments',
  admins: 'admin/auth/admins',
  'live-events': 'admin/live-events',
  webhooks: 'admin/webhooks',
  offers: 'admin/offers',
  products: 'admin/products',
  purchases: 'admin/purchases',
  config: 'admin/config',
  'player-data': 'admin/player-data',
};

// Get the mapped endpoint for a resource
const getEndpoint = (resource: string): string => {
  return resourceMap[resource] || resource;
};

// Normalize list response to Refine format
const normalizeListResponse = <T>(data: unknown): GetListResponse<T> => {
  // Handle different response formats
  if (Array.isArray(data)) {
    // Simple array response
    return {
      data: data as T[],
      total: data.length,
    };
  }

  if (typeof data === 'object' && data !== null) {
    const response = data as Record<string, unknown>;

    // Handle { data: [...], meta: { total: N } } format (our backend format)
    if (Array.isArray(response.data) && response.meta) {
      const meta = response.meta as Record<string, number>;
      return {
        data: response.data as T[],
        total: meta.total || response.data.length,
      };
    }

    // Handle { data: [...], total: N } format
    if (Array.isArray(response.data)) {
      return {
        data: response.data as T[],
        total: (response.total as number) || (response.count as number) || response.data.length,
      };
    }

    // Handle NestJS CRUD format { data: [...], count: N, page: N, pageCount: N }
    if (response.count !== undefined) {
      return {
        data: (response.data || []) as T[],
        total: response.count as number,
      };
    }

    // Handle { items: [...], totalCount: N } format
    if (Array.isArray(response.items)) {
      return {
        data: response.items as T[],
        total:
          (response.totalCount as number) || (response.total as number) || response.items.length,
      };
    }

    // Heuristics: Find any property that is an array
    const keys = Object.keys(response);
    for (const key of keys) {
      if (Array.isArray(response[key])) {
        const potentialData = response[key] as T[];
        // If we found an array and there's a 'total' or 'count' property, it's likely our data
        if (
          response.total !== undefined ||
          response.count !== undefined ||
          response.totalCount !== undefined
        ) {
          return {
            data: potentialData,
            total:
              (response.total as unknown as number) ||
              (response.count as unknown as number) ||
              (response.totalCount as unknown as number) ||
              potentialData.length,
          };
        }
      }
    }
  }

  // Fallback: wrap in array
  return {
    data: [data] as T[],
    total: 1,
  };
};

// Custom data provider with admin API endpoint mapping
export const dataProvider: DataProvider = {
  // Get list of resources
  getList: async ({ resource, pagination, filters, sorters }) => {
    const endpoint = getEndpoint(resource);
    const { current = 1, pageSize = 20 } = pagination || {};

    // Build query params — send both offset/limit and page for backend compatibility
    const params: Record<string, unknown> = {
      offset: (current - 1) * pageSize,
      limit: pageSize,
      page: current,
    };

    // Add filters
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        if ('field' in filter && filter.value !== undefined && filter.value !== null) {
          // Ant Design table filters send arrays — use first value for single-value backend params
          const val = Array.isArray(filter.value) ? filter.value[0] : filter.value;
          if (val !== undefined && val !== '') {
            params[filter.field] = val;
          }
        }
      });
    }

    // Add sorting
    if (sorters && sorters.length > 0) {
      const sorter = sorters[0];
      params.sortBy = sorter.field;
      params.sortOrder = sorter.order === 'desc' ? 'DESC' : 'ASC';
    }

    try {
      const response = await axiosInstance.get(`/${endpoint}`, { params });
      return normalizeListResponse(response.data);
    } catch (error) {
      console.error(`Failed to fetch ${resource}:`, error);
      throw error;
    }
  },

  // Get single resource
  getOne: async <T>({
    resource,
    id,
  }: {
    resource: string;
    id: string | number;
    meta?: Record<string, unknown>;
  }): Promise<GetOneResponse<T>> => {
    const endpoint = getEndpoint(resource);

    try {
      const response = await axiosInstance.get(`/${endpoint}/${id}`);

      // Handle different response formats
      const data = response.data;
      if (data && typeof data === 'object' && 'data' in data) {
        return { data: data.data as T };
      }
      return { data: data as T };
    } catch (error) {
      console.error(`Failed to fetch ${resource}/${id}:`, error);
      throw error;
    }
  },

  // Create resource
  create: async ({ resource, variables }) => {
    const endpoint = getEndpoint(resource);

    try {
      const response = await axiosInstance.post(`/${endpoint}`, variables);
      const data = response.data;
      return { data: data.data || data };
    } catch (error) {
      console.error(`Failed to create ${resource}:`, error);
      throw error;
    }
  },

  // Update resource
  update: async ({ resource, id, variables }) => {
    const endpoint = getEndpoint(resource);

    try {
      const response = await axiosInstance.put(`/${endpoint}/${id}`, variables);
      const data = response.data;
      return { data: data.data || data };
    } catch (error) {
      console.error(`Failed to update ${resource}/${id}:`, error);
      throw error;
    }
  },

  // Delete resource
  deleteOne: async ({ resource, id }) => {
    const endpoint = getEndpoint(resource);

    try {
      const response = await axiosInstance.delete(`/${endpoint}/${id}`);
      return { data: response.data };
    } catch (error) {
      console.error(`Failed to delete ${resource}/${id}:`, error);
      throw error;
    }
  },

  // Get API URL (required by Refine)
  getApiUrl: () => {
    return axiosInstance.defaults.baseURL || '';
  },

  // Custom method for non-CRUD operations
  custom: async ({ url, method, payload, query, headers }) => {
    const response = await axiosInstance({
      url,
      method,
      data: payload,
      params: query,
      headers,
    });

    return { data: response.data };
  },
};
